/**
 * QuickCashVirtualDeck.js
 * 
 * Adapter for using the VirtualDeckSystem with the Quick Cash game.
 * This provides a clean interface for the Quick Cash game to interact with
 * the virtual deck system, maintaining the same API as the original BatchMintingSystem.
 */

import { QUICK_CASH_CONFIG } from './QuickCashConfig';
import * as VirtualDeckSystem from '../../VirtualDeck/VirtualDeckSystem';

// Game ID for Quick Cash
const GAME_ID = 'quick_cash';

// Extend the game config with additional properties needed by the virtual deck system
const getExtendedGameConfig = () => {
  return {
    ...QUICK_CASH_CONFIG,
    // Additional properties needed by the virtual deck system
    winningSymbolsCount: 3,
    matchesNeeded: 3,
    defaultLotteryEntries: 1,
    prizeFormatting: {
      kFormat: true,
      kThreshold: 1000,
      decimals: 0
    }
  };
};

/**
 * Creates a new batch with the segmented approach
 * @param {number} batchSize - The number of cards in the batch (default 10,000)
 * @param {number} segmentSize - The size of each segment (default 100)
 * @returns {Object} The created batch
 */
export const createNewBatch = (batchSize = 10000, segmentSize = 100) => {
  const gameConfig = getExtendedGameConfig();
  return VirtualDeckSystem.createNewBatch(GAME_ID, gameConfig, batchSize, segmentSize);
};

/**
 * Resets the virtual deck by clearing the current batch and creating a new one
 * This ensures that lottery tickets are properly distributed and shuffled
 * @param {number} batchSize - The number of cards in the new batch (default 10,000)
 * @param {number} segmentSize - The size of each segment (default 100)
 * @returns {Object} The newly created batch
 */
export const resetVirtualDeck = (batchSize = 10000, segmentSize = 100) => {
  // Clear all local storage keys for this game
  const keysToRemove = [
    `virtual_deck_${GAME_ID}_batch`,
    `virtual_deck_${GAME_ID}_drawn_cards`,
    `virtual_deck_${GAME_ID}_seed`,
    `virtual_deck_${GAME_ID}_salt`,
    `virtual_deck_${GAME_ID}_deck_hash`
  ];
  
  // Remove all keys from local storage
  keysToRemove.forEach(key => localStorage.removeItem(key));
  
  // Create a new batch
  return createNewBatch(batchSize, segmentSize);
};

/**
 * Gets the current batch
 * @returns {Object} The current batch
 */
export const getCurrentBatch = () => {
  return VirtualDeckSystem.getCurrentBatch(GAME_ID);
};

/**
 * Draws the next card from the batch
 * @returns {Object} The drawn card data
 */
export const drawNextCard = () => {
  const gameConfig = getExtendedGameConfig();
  return VirtualDeckSystem.drawNextCard(GAME_ID, gameConfig);
};

/**
 * Gets the list of drawn cards
 * @returns {Array} The list of drawn cards
 */
export const getDrawnCards = () => {
  return VirtualDeckSystem.getDrawnCards(GAME_ID);
};

/**
 * Gets batch statistics
 * @returns {Object} Batch statistics
 */
export const getBatchStatistics = () => {
  return VirtualDeckSystem.getBatchStatistics(GAME_ID);
};

/**
 * Verifies a card's outcome
 * @param {Object} card - The card to verify
 * @returns {Object} Verification result
 */
export const verifyCardOutcome = (card) => {
  return VirtualDeckSystem.verifyCardOutcome(GAME_ID, card);
};

/**
 * Initializes a game state from a drawn card
 * @param {Object} card - The drawn card
 * @returns {Object} The game state
 */
export const initializeGameFromCard = (card) => {
  return VirtualDeckSystem.initializeGameFromCard(card);
};

/**
 * Updates the status of a card in the drawn cards list
 * @param {string} cardId - The ID of the card to update
 * @param {string} status - The new status ('drawn', 'revealed', 'claimed')
 * @returns {boolean} Whether the update was successful
 */
export const updateCardStatus = (cardId, status) => {
  return VirtualDeckSystem.updateCardStatus(GAME_ID, cardId, status);
};

/**
 * Creates a prize list based on the configuration
 * This is kept for backward compatibility but uses the virtual deck system internally
 * @param {number} batchSize - The batch size
 * @returns {Array} The prize list
 */
export const createPrizeList = (batchSize = 10000) => {
  const gameConfig = getExtendedGameConfig();
  // This doesn't actually create the complete deck, just returns the structure
  // that the original function would have returned
  const prizeList = [];
  
  // Add winning cards according to the prize distribution
  gameConfig.bountyPool.tiers.forEach(tier => {
    tier.prizes.forEach(prize => {
      for (let i = 0; i < prize.count; i++) {
        prizeList.push({
          isWinner: true,
          prizeAmount: prize.amount,
          lotteryEntries: prize.entries || 0
        });
      }
    });
  });
  
  // Add non-winning cards to fill the batch
  const nonWinningCount = batchSize - prizeList.length;
  for (let i = 0; i < nonWinningCount; i++) {
    prizeList.push({
      isWinner: false,
      prizeAmount: 0,
      lotteryEntries: Math.floor(Math.random() * 5) + 1 // 1-5 lottery entries
    });
  }
  
  return prizeList;
};

/**
 * Shuffles a deck - kept for backward compatibility
 * @param {Array} prizeList - The prize list
 * @param {string} seed - The seed
 * @param {string} salt - The salt
 * @returns {Array} The shuffled deck
 */
export const shuffleDeck = (prizeList, seed, salt) => {
  // This is now handled internally by the virtual deck system
  console.warn('shuffleDeck is deprecated, use the virtual deck system instead');
  return prizeList; // Just return the input for compatibility
};
